import { ApiError, createDeskworksClient } from '@deskworks/api-client';
import { t } from '@/lib/i18n';

const TOKEN_KEY = 'deskworks.token';

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

let token: string | null = readToken();
const tokenListeners = new Set<(t: string | null) => void>();
let unauthorizedHandler: (() => void) | null = null;

export function getToken(): string | null {
  return token;
}

export function setToken(next: string | null) {
  token = next;
  try {
    if (next) localStorage.setItem(TOKEN_KEY, next);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — keep in memory only */
  }
  tokenListeners.forEach((l) => l(next));
}

export function onTokenChange(listener: (t: string | null) => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

export function setUnauthorizedHandler(handler: () => void) {
  unauthorizedHandler = handler;
}

export const api = createDeskworksClient({
  baseUrl: '',
  getToken,
  onUnauthorized: () => {
    setToken(null);
    unauthorizedHandler?.();
  },
});

/**
 * GET of raw bytes with the sign-in, for endpoints whose bytes the client has no call for (a desk file's content).
 * Errors as the client's: ApiError, a 401 signs out.
 */
export async function fetchBytes(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { headers });
  } catch (err) {
    throw new ApiError(0, 'The server can’t be reached right now.', err);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let body: unknown = text;
    try {
      body = text ? JSON.parse(text) : undefined;
    } catch {
      /* plain text */
    }
    if (res.status === 401) {
      setToken(null);
      unauthorizedHandler?.();
    }
    const message = body && typeof body === 'object' && 'message' in body && typeof body.message === 'string' ? body.message : res.statusText || `HTTP ${res.status}`;
    throw new ApiError(res.status, message, body);
  }
  return res.blob();
}

/** When the token was issued (seconds), from its payload; null when it can't be read. */
function issuedAt(t: string): number | null {
  try {
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.iat === 'number' ? payload.iat : null;
  } catch {
    return null;
  }
}

/** Renew a sign-in at most this often. */
const RENEW_AFTER_MS = 6 * 60 * 60 * 1000;

/**
 * "Stay signed in": the app renews its token as it is used — when it starts and when it comes back to the foreground
 * (a phone that had it in the background) — so a remembered sign-in only ends after 90 days without Deskworks.
 */
export async function renewSession() {
  const current = token;
  const iat = current ? issuedAt(current) : null;
  if (!current || iat === null || Date.now() - iat * 1000 < RENEW_AFTER_MS) return;
  try {
    const res = await api.auth.refresh();
    if (token === current) setToken(res.token);
  } catch {
    /* an expired one sends the person to the sign-in; anything else is tried again next time */
  }
}

/** Turns any thrown value into a short, friendly message. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return t('The server can’t be reached right now. Please try again in a moment.');
    if (err.status === 401) return t('Your session has expired. Please sign in again.');
    if (err.status === 403) return t('You don’t have permission to do that.');
    if (err.status === 404) return t('We couldn’t find that. It may have been deleted.');
    if (err.status >= 500) return t('Something went wrong on the server ({status}). {message}', { status: err.status, message: err.message });
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return t('Unknown error.');
}

export { ApiError };
